/**
 * Calculate Levenshtein distance between two strings
 * Returns the minimum number of single-character edits required to change one string into another
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  const matrix: number[][] = [];

  // Initialize first column
  for (let i = 0; i <= s2.length; i++) {
    matrix[i] = [i];
  }

  // Initialize first row
  for (let j = 0; j <= s1.length; j++) {
    matrix[0][j] = j;
  }

  // Fill in the rest of the matrix
  for (let i = 1; i <= s2.length; i++) {
    for (let j = 1; j <= s1.length; j++) {
      if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[s2.length][s1.length];
}

/**
 * Calculate similarity score between two strings (0 to 1, where 1 is identical)
 */
export function similarityScore(str1: string, str2: string): number {
  const maxLength = Math.max(str1.length, str2.length);
  if (maxLength === 0) return 1;
  
  const distance = levenshteinDistance(str1, str2);
  return 1 - distance / maxLength;
}

/**
 * Check if query is contained in text (case-insensitive)
 */
export function containsMatch(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

export interface ScoredMatch<T> {
  item: T;
  score: number;
  matchType: 'exact' | 'contains' | 'similar';
}

/**
 * Find best matches using multiple strategies:
 * 1. Exact matches
 * 2. Contains matches
 * 3. Similar matches (using Levenshtein distance)
 * 
 * Returns top N matches sorted by relevance
 */
export function findBestMatches<T>(
  items: T[],
  query: string,
  extractText: (item: T) => string[],
  topN: number = 3
): ScoredMatch<T>[] {
  if (!query.trim()) return [];

  const scoredItems: ScoredMatch<T>[] = items.map(item => {
    const textFields = extractText(item);
    let bestScore = 0;
    let matchType: 'exact' | 'contains' | 'similar' = 'similar';

    for (const text of textFields) {
      const lowerText = text.toLowerCase();
      const lowerQuery = query.toLowerCase();

      // Exact match gets highest score
      if (lowerText === lowerQuery) {
        bestScore = 1.0;
        matchType = 'exact';
        break;
      }

      // Contains match gets high score
      if (containsMatch(text, query)) {
        const containsScore = 0.8 + (0.2 * (1 - lowerQuery.length / lowerText.length));
        if (containsScore > bestScore) {
          bestScore = containsScore;
          matchType = 'contains';
        }
      }

      // Similarity match
      const simScore = similarityScore(text, query);
      if (simScore > bestScore) {
        bestScore = simScore;
        matchType = 'similar';
      }
    }

    return {
      item,
      score: bestScore,
      matchType
    };
  });

  // Filter out very low scores (below 0.3) and sort by score
  return scoredItems
    .filter(item => item.score > 0.3)
    .sort((a, b) => {
      // Prioritize by match type first, then by score
      const typeOrder = { exact: 3, contains: 2, similar: 1 };
      if (typeOrder[a.matchType] !== typeOrder[b.matchType]) {
        return typeOrder[b.matchType] - typeOrder[a.matchType];
      }
      return b.score - a.score;
    })
    .slice(0, topN);
}
